import { useCallback, useEffect, useState } from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, space
} from 'style/config';
import OtpInputGroup from 'components/OtpInputGroup/OtpInputGroup';
import Button from 'components/Button/Button.f';
import Divider from 'components/Divider/Divider.f';
import useSubmitGuard from 'pages/Auth/SignIn/hooks/useSubmitGuard';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// Verify-failure codes that map to the AC-036 "expired OR incorrect" message +
// auto-resend (BR-029: expired and incorrect are byte-identical, no leakage).
// Any other rejection (5xx / generic) falls through to the generic AC-034 copy.
const OTP_RETRYABLE_ERROR_CODES = ['ERR_OTP_LOGIN_SESSION_ERROR', 'ERR_OTP_INVALID_CODE'];

const isRetryableOtpError = error => OTP_RETRYABLE_ERROR_CODES.includes(error?.errorCode);

// The screen switches its section-label / heading / resend-channel COPY on the
// phone-vs-email mode (Figma 270-84837 phone / 181-169990 email). The canonical
// mfaMode VALUE for the phone method is 'phone' (Phase-3 backend contract, FIX 4);
// only the display copy is mode-dependent.
const isPhoneMode = method => method === 'phone';

/**
 * OtpEntryScreen (SIM-7 / ACS-4131, Phase 1 send + Phase 3 entry/verify).
 *
 * Figma nodes 270-84837 (phone) / 181-169990 (email): a 400px column with, top to
 * bottom, a section label, a heading, a subtitle naming the masked destination
 * (BR-021 — never the raw value), a SIX-box 6-digit code entry, a resend line with
 * a cooldown countdown, a divider, and a "Try Another Method" pill that navigates
 * back to the authorization (method-choice) screen.
 *
 * The 6-box entry is the shared, design-system `components/OtpInputGroup` (44px
 * boxes, auto-advance, Backspace-to-previous, paste distribution and iOS one-time-
 * code autofill). The unwrapped component is imported so the screen stays free of a
 * Redux dependency and supplies its own per-box `localization` (SignIn locale key
 * otpInputBoxAriaLabel). It auto-focuses the first box on mount (OTP-03) and calls
 * `onComplete(code)` when all six boxes are filled — that IS the auto-submit; there
 * is NO Verify button. On mount the screen triggers the initial OTP send (via the
 * injected useOtpSession) and shows the masked destination.
 *
 * Completion routes through the SIM-10 completeAuthentication terminal handler with
 * { method:'otp', securityToken, otpCode, mfaMode, keepSignedIn, email? } — where
 * `email` is the RAW entered email ONLY for email-entered customers and is OMITTED
 * for phone customers (F-001 / ADR-SIM7-001; no masked or placeholder email is ever
 * sent). The auto-submit is guarded against double-submit (BR-024/OTP-07) so a fast
 * double-fill can't double-verify.
 *
 * A verify failure that is expired OR incorrect shows the single AC-036 message
 * (naming the masked destination), sets OtpInputGroup's `error` so it clears the
 * boxes + refocuses the first, and auto-resends within the resend limits; a 5xx
 * shows the generic AC-034 error (also setting `error` so the boxes clear and
 * re-enable). While a send/resend/verify is in flight the DARK-scrim shared Loader
 * (Figma 270-85083, rgba(0,0,0,.25)) owned by SignIn.f.jsx is shown — driven via the
 * injected `onBusyChange` callback (FIX 3). On a successful verify the busy state is
 * kept true so the loader stays visible across the post-verify redirect delay (FIX 7);
 * this screen renders no bespoke loader of its own.
 *
 * NOTE (SIM-9): the alt-channel "No Access to this phone? Send passcode via email"
 * shortcut (SMS→email) is owned by SIM-9 and is intentionally NOT rendered here.
 * The full BR-041 cross-method cooldown-carry logic is also SIM-9 — SIM-7 only
 * navigates back to the authorization step via goTo('authorization').
 *
 * Presentation/state-orchestration only: send/cooldown state lives in the injected
 * `session` (useOtpSession); navigation via the injected `goTo`.
 *
 * @param {object} props
 * @param {Function} props.goTo - flow navigation (Try Another Method → 'authorization').
 * @param {string} props.method - 'phone' | 'email' (drives display copy only).
 * @param {object} props.session - the useOtpSession return.
 * @param {object} props.authContext - { securityToken, mfaMode, enteredCredential,
 *   credentialType, keepSignedIn, ... }.
 * @param {Function} props.completeAuthentication - SIM-10 terminal handler.
 * @param {Function} [props.onBusyChange] - reports the in-flight state (send/verify/
 *   redirect) up to SignIn.f.jsx so it can show/hide the shared dark-scrim Loader
 *   (FIX 3 / FIX 7). Kept true across a successful verify's redirect delay.
 * @param {boolean} [props.skipInitialSend] - true when SignIn restored the OTP step
 *   from a refresh (FIX 6 / BR-028): the previously-sent code stays valid, so the
 *   mount effect must NOT re-send. Defaults false (normal advance always sends).
 * @param {string} [props.headingId] - id the page shell's <main aria-labelledby>
 *   points at; when supplied the screen's FIRST heading owns it (per-screen heading
 *   ownership — the OTP frame has no "Sign In" shell heading), so <main> stays
 *   labelled by a real, visible heading (AC-023).
 * @param {object} [props.headingRef] - ref forwarded onto that same first heading so
 *   route-entry focus lands on it (SI-2 focus-management contract).
 */
const OtpEntryScreen = ({
    goTo = noop,
    method,
    session,
    authContext,
    completeAuthentication = noop,
    onBusyChange = noop,
    skipInitialSend = false,
    headingId,
    headingRef
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    // Drives OtpInputGroup's `error` prop: on any verify failure the boxes clear +
    // refocus the first (AC-036 clear-and-retry). Cleared when the user resumes
    // typing (clearError) or a new verify attempt begins.
    const [hasVerifyError, setHasVerifyError] = useState(false);

    const { otpSession = {}, sendInitial, resend } = session;
    const { maskedDestination } = otpSession;
    const mfaMode = authContext?.mfaMode || method;
    const phoneMode = isPhoneMode(method);

    // Mode-dependent copy (Figma phone vs email).
    const sectionLabel = getText(phoneMode ? 'otpSectionLabelPhone' : 'otpSectionLabelEmail');
    const heading = getText(phoneMode ? 'otpHeadingPhone' : 'otpHeadingEmail');
    const resendChannel = getText(phoneMode ? 'resendChannelPhone' : 'resendChannelEmail');

    // Send the initial OTP on mount. The DARK-scrim shared Loader (owned by
    // SignIn.f.jsx) is shown while the send is in flight, driven via onBusyChange
    // (FIX 3). A send failure keeps the customer on the screen with the send-failure
    // copy (SEND-04) and clears busy.
    useEffect(() => {
        // FIX 6 (BR-028): on a refresh-restore the previously-sent code is still valid,
        // so DO NOT re-send — just render the OTP screen with the masked destination.
        if (skipInitialSend) {
            return undefined;
        }

        let active = true;

        onBusyChange(true);
        Promise.resolve(sendInitial(authContext))
            .catch(() => {
                if (active) {
                    setErrorMessage(getText('otpSendFailed'));
                }
            })
            .finally(() => {
                if (active) {
                    onBusyChange(false);
                }
            });

        return () => {
            active = false;
        };
        // Mount-only send; authContext/session are stable for the screen's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Verify a specific code (guarded against double-submit — BR-024/OTP-07). The
    // code is passed in so the auto-submit uses the value that just completed the
    // 6th box rather than a possibly-stale closure over state.
    const verifyAction = useCallback(
        async otpCode => {
            setErrorMessage(null);
            setHasVerifyError(false);
            // Show the dark-scrim Loader while verify is in flight (FIX 3).
            onBusyChange(true);

            // F-001: thread the RAW entered email ONLY for email customers; omit it
            // for phone customers (a masked/placeholder email is never sent — BR-021).
            const isEmailCustomer = String(authContext?.credentialType).toUpperCase() === 'EMAIL';
            const params = {
                method: 'otp',
                securityToken: authContext?.securityToken,
                otpCode,
                mfaMode,
                keepSignedIn: authContext?.keepSignedIn,
                ...(isEmailCustomer && { email: authContext?.enteredCredential })
            };

            try {
                // On success DO NOT clear busy: completeAuthentication resolves after
                // the session is stored but a redirect to the homepage still follows,
                // so the loader must stay up across that delay (FIX 7).
                return await completeAuthentication(params);
            } catch (error) {
                // Verify failed — hide the loader so the customer can retry.
                onBusyChange(false);
                // Any verify failure clears the boxes + refocuses the first via
                // OtpInputGroup's `error` prop so the customer can retry cleanly.
                setHasVerifyError(true);

                if (isRetryableOtpError(error)) {
                    // AC-036 (BR-029): identical copy for expired vs incorrect, naming
                    // the masked destination, then auto-resend within the cooldown/limit.
                    setErrorMessage(`${getText('otpExpiredOrIncorrect')}${maskedDestination}`);
                    // Fire-and-forget within the cooldown/cap; swallow a rejected send so
                    // it never surfaces as an unhandled rejection. Resend failure state is
                    // owned by the session (cooldown/limit), so there is nothing to route.
                    Promise.resolve(resend()).catch(noop);
                } else {
                    // 5xx / generic backend error (AC-034); the guard re-enables entry.
                    setErrorMessage(getText('genericError'));
                }

                throw error;
            }
        },
        [authContext, mfaMode, maskedDestination, completeAuthentication, resend, onBusyChange]
    );

    // Resend action (guarded — SEND-02). Delegates to the session, which enforces
    // the cooldown + 3-resend cap. Shows the dark-scrim Loader while the resend send
    // is in flight (FIX 3), then hides it.
    const resendAction = useCallback(async () => {
        onBusyChange(true);

        try {
            return await resend();
        } finally {
            onBusyChange(false);
        }
    }, [resend, onBusyChange]);

    const { submit: submitVerify, inFlight: verifyInFlight } = useSubmitGuard(verifyAction);
    const { submit: submitResend } = useSubmitGuard(resendAction);

    // OtpInputGroup.onComplete fires with the full string once all six boxes are
    // filled — this IS the auto-submit. The double-submit guard inside useSubmitGuard
    // collapses a fast double-fill to a single verify (OTP-07). The completed code is
    // passed explicitly so verify never reads a stale closure.
    const triggerVerify = useCallback(
        otpCode => {
            // Swallow the rethrow the guard surfaces for error routing; the message
            // state is already set inside verifyAction.
            submitVerify(otpCode).catch(noop);
        },
        [submitVerify]
    );

    // Clear our verify error copy/state when the user resumes typing (OtpInputGroup
    // calls this on the next keystroke after an error).
    const clearVerifyError = useCallback(() => {
        setErrorMessage(null);
        setHasVerifyError(false);
    }, []);

    const handleResend = useCallback(() => {
        // Swallow the guard's rethrow: resend errors surface via the session's own
        // cooldown/limit state (BR-025), so there is nothing further to route here.
        submitResend().catch(noop);
    }, [submitResend]);

    const handleTryAnotherMethod = useCallback(() => {
        goTo('authorization');
    }, [goTo]);

    const cooldownActive = session.remainingCooldown > 0;

    return (
        <div css={styles.root}>
            {/* Per-screen heading ownership (Figma 270-84837 / 181-169990 have NO
                "Sign In" shell heading): the "Phone/Email Verification" label is the
                page's single top heading — an <h1> carrying the shell's headingId so
                <main aria-labelledby> resolves to a real visible heading (AC-023) and
                receiving headingRef so route-entry focus lands here (SI-2). Styled to
                the Figma 16px bold via styles.sectionLabel; semantically the page h1. */}
            <h1
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                css={styles.sectionLabel}
            >
                {sectionLabel}
            </h1>
            {/* Second line ("Check your …") is the sub-heading below the h1 — kept as a
                heading (h2) for a valid, gap-free hierarchy; styled to match Figma. */}
            <h2 css={styles.heading}>{heading}</h2>

            {/* The masked-destination subtitle ALWAYS renders — even alongside an
                error (FIX 5): the customer keeps the "You will receive a code at
                {masked}…" context while the separate role=alert error appears below.
                Both may name the masked destination; that is intentional. */}
            <p css={styles.subtitle}>
                {getText('otpSubtitlePrefix')}
                <strong>{maskedDestination}</strong>
                {getText('otpSubtitleSuffix')}
            </p>

            {/* Shared six-box code entry (components/OtpInputGroup): auto-advance,
                Backspace-to-previous, paste distribution and iOS one-time-code
                autofill. onComplete auto-submits; `error` clears the boxes +
                refocuses on a verify failure; `disabled` blocks a double-fill while
                a verify is in flight (belt-and-braces with useSubmitGuard). */}
            <div css={styles.otpBoxRow}>
                <OtpInputGroup
                    onComplete={triggerVerify}
                    error={hasVerifyError}
                    clearError={clearVerifyError}
                    disabled={verifyInFlight}
                    localization={{ accessibilityOtpInput: getText('otpInputBoxAriaLabel') }}
                />
            </div>

            {errorMessage && (
                <div
                    role='alert'
                    aria-live='assertive'
                    css={styles.error}
                >
                    {errorMessage}
                </div>
            )}

            {/* Resend control + countdown announced via an aria-live region (SESS-07).
                Copy switches on mode: "Didn't get the SMS/an email? Resend code…". */}
            <div
                aria-live='polite'
                css={styles.resendRow}
            >
                {session.limitReached ? (
                    <span css={styles.limit}>{getText('otpLimitMessage')}</span>
                ) : (
                    <span>
                        {getText('resendPrefix')}
                        <strong>{resendChannel}</strong>
                        {getText('resendQuestion')}
                        {cooldownActive ? (
                            <span>
                                {getText('resendCountdownPrefix')}
                                <strong>
                                    {session.remainingCooldown}
                                    {getText('resendCountdownSuffix')}
                                </strong>
                            </span>
                        ) : (
                            <Button
                                variant='link'
                                onClick={handleResend}
                                css={styles.resendButton}
                            >
                                {getText('resendButton')}
                            </Button>
                        )}
                    </span>
                )}
            </div>

            <Divider
                color='lightGray'
                css={styles.divider}
            />

            {/* White pill with a 2px black border (Figma): the design-system
                Button secondary variant IS that treatment (transparent bg,
                borderColor: currentcolor → black text, 2px, full-radius pill). */}
            <Button
                variant='secondary'
                block={true}
                onClick={handleTryAnotherMethod}
            >
                {getText('tryAnotherMethodButton')}
            </Button>
        </div>
    );
};

const styles = {
    // 400px column, 16px gap between blocks (Figma 270-84837 / 181-169990).
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4]
    },
    // Top heading (the page h1) + sub-heading: 20px bold black (Figma type size).
    // outline:none suppresses the programmatic-focus box on the h1 (it is
    // tabIndex=-1 + focused on route entry for SI-2; only the visible outline is
    // removed — a11y focus is unchanged), matching the shell heading treatment.
    sectionLabel: {
        margin: 0,
        color: colors.black,
        fontSize: fontSizes.lg,
        fontWeight: fontWeights.bold,
        outline: 'none'
    },
    heading: {
        margin: 0,
        color: colors.black,
        fontSize: fontSizes.lg,
        fontWeight: fontWeights.bold
    },
    // Full-width wrapper that left-aligns the shared OtpInputGroup's fieldset row
    // (its internal Flex uses justifyContent:'center'). Overriding it to
    // 'flex-start' here — WITHOUT touching OtpInputGroup — spreads the six 44px
    // boxes (16px gaps) as a left-aligned ~344px row across the 400px column,
    // matching Figma 270-84837 and the authorization screen's content width.
    otpBoxRow: {
        width: '100%',
        '& fieldset': {
            width: '100%',
            justifyContent: 'flex-start'
        }
    },
    // 14px regular black; the masked destination is bold inline (via <strong>).
    subtitle: {
        margin: 0,
        color: colors.black,
        fontSize: fontSizes.base
    },
    error: {
        display: 'flex',
        color: colors.error,
        fontSize: fontSizes.sm
    },
    resendRow: {
        color: colors.black,
        fontSize: fontSizes.base
    },
    // Resend is the design-system Button variant='link' (blue underlined). Strip the
    // button's default inline-flex/min-height so it sits inline in the resend copy.
    resendButton: {
        display: 'inline',
        minHeight: 0,
        padding: 0
    },
    limit: {
        color: colors.error
    },
    // Full-width 1px #EEE rule via the shared Divider (colors.divider === #EEE).
    divider: {
        margin: 0
    }
};

export default wrapFunctionalComponent(OtpEntryScreen, 'OtpEntryScreen');
